import { useEffect, useMemo, useState, ReactElement } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel, {
	tableSortLabelClasses,
} from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import { visuallyHidden } from '@mui/utils';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { getComparator, stableSort } from 'pages/AnnualUserAccessReview/_util';
import SelectAllButtonGroup from './SelectAllButtonGroup';
import PersonnelTableCell from './PersonnelTableCell';
import { STATUS_TYPES } from 'pages/AnnualUserAccessReview/_const';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
	[`&.${tableCellClasses.head}`]: {
		color: theme.palette.primary.dark,
		backgroundColor: theme.palette.secondary.light,
	},
	[`.${tableSortLabelClasses.active}`]: {
		color: theme.palette.primary.dark,
	},
}));

/**
 * Reusable paging table to display users for User Access Review
 * @param {string} props.title title to display
 * @param {string} props.error error message
 * @param {object[]} props.initialValues values to seed the table
 * @param {function} props.onChange on change handler
 * @param {object[]} props.headCells table definition
 * @param {Object} props.options optional features
 * @param {string} props.defaultOrderBy column to order by default
 * @param {string} props.defaultOrder default order direction
 * @param {boolean} props.nonEmployee non employee identifier for conditional rendering
 * @param {boolean} props.isReview review mode identifier for conditional rendering
 * @returns {ReactElement} table with pagination
 */
const PagingTable = ({
	title,
	error,
	initialValues,
	onChange,
	headCells,
	options,
	defaultOrderBy = 'dateValidationExpires',
	defaultOrder = 'asc',
	nonEmployee = false,
	isReview = false,
}) => {
	//logically check if option combination makes sense
	if (options) {
		if (
			options.singleSelection &&
			(options.enableSelectAll || options.enableCustomSelectAll)
		) {
			console.warn(
				'DEVELOPER WARNING: Options may be misconfigured. Single-selection with Select-All or Custom Select-All may not make sense. Please ensure your `options` props are configured correctly.',
			);
		}
	}

	const [sOrder, setOrder] = useState(defaultOrder);
	const [sOrderBy, setOrderBy] = useState(defaultOrderBy);
	const [sPage, setPage] = useState(0);
	const [sRows, setRows] = useState([]);
	const [sRowsPerPage, setRowsPerPage] = useState(10);
	const [sSearch, setSearch] = useState('');
	const [sSelectAllChecked, setSelectAllChecked] = useState(false);
	const selectionCount = sRows.filter(r => r.isSelected).length;

	useEffect(() => {
		setRows(
			stableSort(
				[...initialValues.map(v => ({ isSelected: false, ...v }))],
				getComparator('asc', 'name'),
			),
		);
	}, [initialValues]);

	const handleRequestSort = (event, sortProperty) => {
		let newOrder =
			sOrderBy !== sortProperty
				? 'asc'
				: sOrder === 'asc'
					? 'desc'
					: 'asc';
		let newOrderBy = sortProperty;

		setOrder(newOrder);
		setOrderBy(newOrderBy);
	};

	const handleSelectAllClick = event => {
		const sorted = stableSort(sRows, getComparator(sOrder, sOrderBy));
		const start = sPage * sRowsPerPage;
		const end = start + sRowsPerPage;
		const visible = sorted.slice(start, end);
		const visibleIds = visible.map(r => r.id);

		const newRows = sRows.map(r =>
			visibleIds.includes(r.id)
				? { ...r, isSelected: event.target.checked }
				: r,
		);

		setRows(newRows);
		setSelectAllChecked(event.target.checked);
		onChange(newRows);
	};
	const handleSelectAllExpiredClick = event => {
		let newRows = sRows.map(r => ({
			...r,
			isSelected:
				r.status === STATUS_TYPES.OVERDUE
					? event.target.checked
					: false,
		}));
		setRows(newRows);
		setSelectAllChecked(event.target.checked);
		onChange(newRows);
	};
	const handleSelectAllExpiringClick = event => {
		let newRows = sRows.map(r => ({
			...r,
			isSelected:
				r.status === STATUS_TYPES.OVERDUE ||
				r.status === STATUS_TYPES.VALID_EXPIRING_SOON
					? event.target.checked
					: false,
		}));
		setRows(newRows);
		setSelectAllChecked(event.target.checked);
		onChange(newRows);
	};

	const handleClick = (event, id) => {
		if (options.singleSelection && selectionCount >= 1) {
			let newRows = sRows.map(r => ({
				...r,
				isSelected: r.id === id,
			}));
			setRows(newRows);
			onChange(newRows);
		} else {
			let newRows = sRows.map(r => ({
				...r,
				isSelected: r.id === id ? event.target.checked : r.isSelected,
			}));
			setRows(newRows);
			onChange(newRows);
		}
	};

	const handleChangePage = (_, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = event => {
		const newRowsPerPage = parseInt(event.target.value, 10);

		// Reset selections if switching to a smaller page size
		if (newRowsPerPage < sRowsPerPage) {
			const newRows = sRows.map(r => ({
				...r,
				isSelected: false,
			}));
			setRows(newRows);
			setSelectAllChecked(false);
			onChange(newRows);
		}

		setRowsPerPage(newRowsPerPage);
		setPage(0);
	};

	// Avoid a layout jump when reaching the last page with empty rows.
	const emptyRows =
		sPage > 0 ? Math.max(0, (1 + sPage) * sRowsPerPage - sRows.length) : 0;

	const visibleRows = useMemo(() => {
		return stableSort(sRows, getComparator(sOrder, sOrderBy)).slice(
			sPage * sRowsPerPage,
			sPage * sRowsPerPage + sRowsPerPage,
		);
	}, [sOrder, sOrderBy, sPage, sRowsPerPage, sRows]);

	const createSortHandler = property => event => {
		handleRequestSort(event, property);
	};

	let bgColor = error
		? {
				bgcolor: theme => theme.palette.error.light,
			}
		: selectionCount > 0
			? {
					bgcolor: theme => theme.palette.secondary.light,
				}
			: {};

	return (
		<Box sx={{ width: '100%' }}>
			<Paper sx={{ width: '100%', mb: 2 }}>
				<Toolbar
					sx={{
						pl: { sm: 2 },
						pr: { xs: 1, sm: 1 },
						...bgColor,
					}}
				>
					<Stack direction={'column'} gap={1}>
						<Typography
							sx={{ flex: '1 1 100%' }}
							variant='subtitle1'
							id='tableTitle'
							component='div'
						>
							{title} ({selectionCount} selected)
						</Typography>
						<Typography
							sx={{
								flex: '1 1 100%',
								color: error ? 'red' : 'black',
							}}
							variant='subtitle2'
							id='tableErrorLabel'
							component='div'
						>
							{error
								? error
								: options.singleSelection
									? 'You can only select one user.'
									: null}
						</Typography>
					</Stack>
				</Toolbar>
				{options && (
					<Toolbar
						sx={{
							pl: { sm: 2 },
							pr: { xs: 1, sm: 1 },
							...bgColor,
							justifyContent: 'space-between',
							flexDirection: 'row-reverse',
						}}
					>
						{options?.enableKeywordSearch && (
							<TextField
								id='outlined-controlled'
								label='Keyword Search'
								size='small'
								value={sSearch}
								onChange={event => {
									const searchValue = event.target.value;
									setSearch(searchValue);
									if (!!!searchValue) {
										setOrderBy(defaultOrderBy);
										setOrder(defaultOrder);
										setPage(0);
									} else {
										setRows(
											sRows.map(r => ({
												...r,
												searchMatches: Object.values(
													r,
												).filter(
													v =>
														typeof v === 'string' &&
														v
															.toLocaleLowerCase()
															.includes(
																searchValue.toLocaleLowerCase(),
															),
												).length,
											})),
										);
										setOrderBy('searchMatches');
										setOrder('desc');
										setPage(0);
									}
								}}
							/>
						)}
						{options?.enableCustomSelectAll && (
							<SelectAllButtonGroup
								{...{ sRowsPerPage }}
								selectAllHandler={handleSelectAllClick}
								selectAllExpiredHandler={
									handleSelectAllExpiredClick
								}
								selectAllExpiringHandler={
									handleSelectAllExpiringClick
								}
							/>
						)}
					</Toolbar>
				)}
				<TableContainer sx={{ maxHeight: 440 }}>
					<Table
						sx={{ minWidth: 500, maxWidth: '100%' }}
						aria-labelledby='tableTitle'
						size={'small'}
						stickyHeader
					>
						<TableHead>
							<TableRow>
								<StyledTableCell padding='checkbox'>
									{options?.enableSelectAll && (
										<Checkbox
											color='primary'
											indeterminate={
												selectionCount > 0 &&
												selectionCount < sRows.length
											}
											checked={sSelectAllChecked}
											onChange={handleSelectAllClick}
											inputProps={{
												'aria-label':
													'select all desserts',
											}}
										/>
									)}
								</StyledTableCell>
								{headCells.map(headCell => (
									<StyledTableCell
										key={headCell.id}
										align={
											headCell.numeric ? 'right' : 'left'
										}
										padding={
											headCell.hasHeaderIcon
												? 'none'
												: 'normal'
										}
										sortDirection={
											sOrderBy === headCell.id
												? sOrder
												: false
										}
									>
										<TableSortLabel
											active={sOrderBy === headCell.id}
											direction={
												sOrderBy === headCell.id
													? sOrder
													: 'asc'
											}
											onClick={createSortHandler(
												headCell.id,
											)}
										>
											<Typography
												variant='body1'
												sx={{
													textDecoration: 'underline',
												}}
											>
												{headCell.label}
											</Typography>
											{sOrderBy === headCell.id ? (
												<Box
													component='span'
													sx={visuallyHidden}
												>
													{sOrder === 'desc'
														? 'sorted descending'
														: 'sorted ascending'}
												</Box>
											) : null}
										</TableSortLabel>
									</StyledTableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{visibleRows.map((row, index) => {
								const isItemSelected = row.isSelected;
								const labelId = `enhanced-table-checkbox-${index}`;

								return (
									<TableRow
										hover
										onClick={event =>
											handleClick(event, row.id)
										}
										role='checkbox'
										aria-checked={isItemSelected}
										tabIndex={-1}
										key={row.id}
										selected={isItemSelected}
										sx={{ cursor: 'pointer' }}
									>
										<TableCell padding='checkbox'>
											<Checkbox
												color='primary'
												checked={isItemSelected}
												inputProps={{
													'aria-labelledby': labelId,
												}}
											/>
										</TableCell>
										<PersonnelTableCell
											headCells={headCells}
											id='name'
											row={row}
											labelId={labelId}
											searchString={sSearch}
										/>
										<PersonnelTableCell
											headCells={headCells}
											id='dateValidationExpires'
											row={row}
											searchString={sSearch}
										/>
										<PersonnelTableCell
											headCells={headCells}
											id='jobTitle'
											row={row}
											searchString={sSearch}
										/>
										<PersonnelTableCell
											headCells={headCells}
											id='department'
											row={row}
											searchString={sSearch}
										/>
										{nonEmployee && (
											<PersonnelTableCell
												headCells={headCells}
												id='companyName'
												row={row}
												searchString={sSearch}
											/>
										)}
										{isReview && (
											<PersonnelTableCell
												headCells={headCells}
												id='delegatedReviewerName'
												row={row}
												searchString={sSearch}
											/>
										)}
									</TableRow>
								);
							})}
							{emptyRows > 0 && (
								<TableRow
									style={{
										height: 33 * emptyRows,
									}}
								>
									<TableCell colSpan={6} />
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={[10, 25, 50, 100]}
					component='div'
					count={sRows.length}
					rowsPerPage={sRowsPerPage}
					page={sPage}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</Paper>
		</Box>
	);
};

PagingTable.propTypes = {
	title: PropTypes.string.isRequired,
	error: PropTypes.string,
	initialValue: PropTypes.arrayOf(PropTypes.object),
	onChange: PropTypes.func.isRequired,
	headCells: PropTypes.arrayOf(PropTypes.object).isRequired,
	options: PropTypes.object,
	defaultOrderBy: PropTypes.string,
	defaultOrder: PropTypes.string,
};

export default PagingTable;
